import type { Media } from "@mw/shared/payload-types";
import type {
  ChatMessagePart,
  ChatScenarioAvatar,
  ChatScenarioData,
  ChatScenarioMedia,
  ChatScenarioMessage,
} from "./types";

export interface CmsChatScenarioPart {
  content?: null | string;
  data?: {
    errorText?: null | string;
    loadingText?: null | string;
    successImage?: (string | null) | Media;
    successText?: null | string;
  } | null;
  name?: null | string;
  type: "speech" | "text" | "tool";
}

function getSuccessImage(partData: CmsChatScenarioPart["data"]): ChatScenarioMedia | null {
  const successImage = partData?.successImage;

  if (typeof successImage === "string") {
    return successImage || null;
  }

  if (successImage?.url) {
    return successImage;
  }

  return null;
}

function mapAvatar(avatar: CmsChatScenarioMessage["avatar"]): ChatScenarioAvatar | undefined {
  if (!avatar || typeof avatar === "string" || !avatar.url) {
    return undefined;
  }

  return {
    url: avatar.url,
    alt: avatar.alt ?? undefined,
    width: avatar.width ?? undefined,
    height: avatar.height ?? undefined,
    blurDataURL: avatar.blurDataURL ?? undefined,
  };
}

export interface CmsChatScenarioMessage {
  avatar?: (string | null) | Media;
  delayBeforeMs?: number | null;
  loadingBeforeMs?: number | null;
  parts: CmsChatScenarioPart[];
  role: "assistant" | "user";
  visibleDurationMs?: number | null;
}

export interface CmsChatScenario {
  agentName: string;
  initialMessages?: CmsChatScenarioMessage[] | null;
  messages: CmsChatScenarioMessage[];
}

function mapPart(part: CmsChatScenarioPart): ChatMessagePart | null {
  if ((part.type === "text" || part.type === "speech") && part.content) {
    return { type: part.type, content: part.content };
  }

  if (part.type === "tool" && part.data?.loadingText) {
    return {
      type: "tool",
      name: "simple",
      data: {
        loadingText: part.data.loadingText,
        successText: part.data.successText ?? undefined,
        successImage: getSuccessImage(part.data),
        errorText: part.data.errorText ?? undefined,
      },
    };
  }

  return null;
}
function mapNumericMs(value: number | null | undefined): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function mapMessage(message: CmsChatScenarioMessage): ChatScenarioMessage {
  return {
    role: message.role,
    parts: message.parts.map(mapPart).filter((part): part is ChatMessagePart => part !== null),
    avatar: mapAvatar(message.avatar),
    loadingBeforeMs: mapNumericMs(message.loadingBeforeMs),
    delayBeforeMs: mapNumericMs(message.delayBeforeMs),
    visibleDurationMs: mapNumericMs(message.visibleDurationMs),
  };
}

export function buildChatScenarioData(chatScenario: CmsChatScenario): ChatScenarioData {
  return {
    initialMessages: chatScenario.initialMessages?.map(mapMessage),
    messages: chatScenario.messages.map(mapMessage),
  };
}
